import type { Article } from './articleTypes';

export const occasionChecks: Article = {
  slug: 'verifier-voiture-occasion',
  category: 'Occasion',
  title: 'Voiture d’occasion : les vérifications à faire avant d’acheter',
  seoTitle: 'Voiture d’occasion : 12 vérifications avant achat',
  description:
    'HistoVec, contrôle technique, factures, essai, pneus, documents et garanties : la check-list complète avant d’acheter une voiture d’occasion.',
  intro:
    'Une bonne référence peut être un mauvais achat si l’exemplaire est mal documenté. Cette check-list aide à faire apparaître les incohérences avant le paiement — sans prétendre remplacer une inspection physique par un professionnel.',
  image: '/assets/guides/guide-occasion-verifications-v2.webp',
  imageAlt:
    'Une personne inspecte à la lampe le compartiment moteur ouvert d’une voiture d’occasion',
  publishedAt: '2026-07-17',
  updatedAt: '2026-07-17',
  readingTime: '14 min',
  takeaways: [
    'Demandez le lien HistoVec avant le déplacement, puis rapprochez ses kilométrages des factures et du contrôle technique.',
    'Le contrôle technique est un bilan réglementaire sans démontage ; il ne certifie pas qu’une voiture est en parfait état.',
    'Un vendeur professionnel ne peut pas remplacer la garantie légale de conformité par une simple garantie commerciale « moteur-boîte-pont ».',
  ],
  sections: [
    {
      id: 'avant-deplacement',
      title: 'Avant de vous déplacer : obtenir les preuves de base',
      content: (
        <>
          <p>
            Un appel de dix minutes peut éviter deux heures de route. Je demande l’immatriculation ou, au
            minimum, la version exacte, le mois de première mise en circulation, le kilométrage, le nombre
            de propriétaires connus et la raison de la vente. Je demande aussi des photos nettes des quatre
            côtés, des roues, de l’intérieur, du tableau de bord allumé et des principaux défauts annoncés.
          </p>
          <p>
            Le vendeur doit générer lui-même le rapport HistoVec puis partager son lien. Le service officiel
            reprend notamment les données administratives, les gages et oppositions, certaines procédures,
            les résultats de contrôles techniques et les kilométrages associés. Seul le titulaire de la carte
            grise peut générer le rapport : un vendeur qui renvoie systématiquement cette démarche après le
            paiement crée un risque inutile.
          </p>
          <aside className="as-article-note as-note-fact">
            <strong>Limite de HistoVec</strong>
            <p>
              Le rapport ne contient pas tous les détails d’une réparation ni le rapport complet d’un expert.
              HistoVec précise lui-même que ces détails ne lui sont pas transmis. Le document est une base de
              cohérence, pas un certificat d’absence d’accident ou de réparation.
            </p>
          </aside>
          <ul className="as-article-checklist">
            <li>Lien HistoVec généré récemment par le titulaire.</li>
            <li>Photos ou scans des factures importantes, données personnelles masquées si nécessaire.</li>
            <li>Procès-verbal complet du dernier contrôle technique, pas seulement la vignette.</li>
            <li>Deux clés disponibles et liste des accessoires annoncés.</li>
            <li>Confirmation écrite du prix et des éventuels frais ajoutés par un professionnel.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'identite',
      title: '1. Faire correspondre vendeur, voiture et documents',
      content: (
        <>
          <p>
            Le nom du vendeur doit correspondre au titulaire du certificat d’immatriculation, sauf situation
            expliquée et documentée — succession, entreprise, professionnel mandaté. Je compare le numéro VIN
            de la carte grise à celui visible sur la voiture. Toute différence de caractère doit être résolue
            avant de poursuivre.
          </p>
          <p>
            La dénomination commerciale d’une annonce ne suffit pas. Je rapproche énergie, puissance, date de
            première immatriculation et finition des documents. Cette vérification évite de calculer assurance,
            Crit’Air ou valeur sur une version différente de celle vendue.
          </p>
        </>
      ),
    },
    {
      id: 'historique',
      title: '2. Reconstituer une chronologie, pas collectionner des tampons',
      content: (
        <>
          <p>
            Je place sur une ligne simple les dates, kilométrages et opérations des factures, contrôles
            techniques et du rapport HistoVec. L’objectif n’est pas d’exiger une facture pour chaque balai
            d’essuie-glace, mais de vérifier que le kilométrage progresse normalement et que les entretiens
            importants correspondent aux préconisations du véhicule.
          </p>
          <p>
            La DGCCRF rappelle que le vendeur n’est pas obligé de remettre carnet et factures, mais conseille
            à l’acheteur de les demander. Leur absence n’est donc pas automatiquement illégale ; elle réduit
            l’information disponible et doit peser dans le prix, le niveau de risque ou la décision de passer
            à une autre annonce.
          </p>
          <div className="as-article-warning">
            <strong>Incohérences qui demandent une explication</strong>
            <ul>
              <li>kilométrage qui baisse entre deux documents ;</li>
              <li>longue période sans trace sur une voiture annoncée « toujours entretenue » ;</li>
              <li>facture majeure sans identification claire du véhicule ;</li>
              <li>vendeur propriétaire depuis très peu de temps sans raison convaincante ;</li>
              <li>usure du volant, du siège ou des pédales difficile à concilier avec le kilométrage.</li>
            </ul>
          </div>
        </>
      ),
    },
    {
      id: 'controle-technique',
      title: '3. Lire le contrôle technique ligne par ligne',
      content: (
        <>
          <p>
            Pour la vente d’une voiture de plus de quatre ans à un particulier, le vendeur doit fournir un
            contrôle technique de moins de six mois. Lorsqu’une contre-visite est prescrite, le délai de deux
            mois ne doit pas être dépassé. Un véhicule de moins de quatre ans peut être vendu à un particulier
            sans contrôle technique ; une vente à un professionnel suit également des règles différentes.
          </p>
          <p>
            Je ne lis pas seulement « favorable ». Les défaillances mineures, l’usure des pneus, un jeu ou une
            corrosion signalée donnent des indices sur l’entretien futur. Je compare aussi les kilométrages
            des contrôles successifs dans HistoVec.
          </p>
          <aside className="as-article-note as-note-fact">
            <strong>Ce que le contrôle technique ne promet pas</strong>
            <p>
              La DGCCRF indique que les points sont vérifiés sans démontage et que la vignette ne signifie pas
              que le véhicule est en parfait état. Le contrôle réglementaire ne remplace donc ni un essai,
              ni l’historique, ni une inspection mécanique lorsque l’enjeu le justifie.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'statique',
      title: '4 à 7. Examiner la voiture à l’arrêt et en plein jour',
      content: (
        <>
          <h3>4. Carrosserie et vitrages</h3>
          <p>
            Je regarde la voiture sèche, propre et en lumière naturelle. Des écarts de teinte, des joints
            peints, des alignements irréguliers ou des fixations marquées peuvent signaler une réparation.
            Une réparation n’interdit pas l’achat ; elle doit être comprise, correctement réalisée et cohérente
            avec le prix. Je photographie les défauts convenus pour éviter une mémoire sélective au moment de signer.
          </p>
          <h3>5. Pneus, roues et posture</h3>
          <p>
            Les quatre pneus doivent avoir des dimensions autorisées, une usure régulière et un niveau restant
            compatible avec l’usage prévu. Je relève leur marque, leur modèle et le code de date. Deux pneus à
            remplacer immédiatement ne sont pas un détail sur un SUV à grandes jantes. Une usure très différente
            d’un côté peut justifier un contrôle du parallélisme ou du train roulant.
          </p>
          <h3>6. Habitacle et équipements</h3>
          <p>
            Je teste chaque ouverture, ceinture, vitre, rétroviseur, caméra, climatisation, siège, prise et écran.
            Je vérifie que les voyants s’allument au contact puis s’éteignent normalement. Un voyant absent n’est
            pas toujours rassurant : il doit d’abord fonctionner lors de l’autotest.
          </p>
          <h3>7. Sous le capot, sans improviser un diagnostic</h3>
          <p>
            Moteur froid si possible, je cherche surtout des incohérences visibles : niveau très bas, fuite fraîche,
            odeur forte, câblage bricolé, dépôt inhabituel ou pièces manifestement mal fixées. Je ne conclus pas à
            la santé mécanique sur la couleur d’une huile ou un bruit écouté trente secondes. En cas de doute, je
            fais intervenir un professionnel équipé et assuré pour cette inspection.
          </p>
        </>
      ),
    },
    {
      id: 'essai',
      title: '8 à 10. Faire un essai qui ressemble à votre vie',
      content: (
        <>
          <h3>8. Démarrage à froid</h3>
          <p>
            Je demande que la voiture ne soit pas chauffée avant le rendez-vous. Le démarrage, la stabilité du
            ralenti, les voyants et les bruits des premières secondes sont plus informatifs à froid. Si le moteur
            est déjà chaud, je demande pourquoi et je le note ; cela ne prouve rien seul, mais retire une observation.
          </p>
          <h3>9. Ville, route dégradée et vitesse stabilisée</h3>
          <p>
            L’essai doit comprendre des manœuvres, plusieurs freinages progressifs, un passage sur revêtement
            irrégulier et une portion à vitesse stabilisée lorsque c’est légalement possible. Je coupe la radio.
            J’observe la direction, le freinage, la boîte, les vibrations, les bruits d’air et la température.
            Le vendeur reste libre d’organiser l’essai et l’assurance doit couvrir le conducteur.
          </p>
          <h3>10. Tester l’usage, pas seulement la mécanique</h3>
          <p>
            J’installe le siège enfant, la poussette, le vélo plié ou les grands passagers qui motivent l’achat.
            Je tente une place de stationnement comparable à celle du domicile. Une voiture peut fonctionner
            parfaitement et rester une mauvaise réponse au besoin.
          </p>
          <aside className="as-article-note as-note-opinion">
            <strong>Mon seuil d’arrêt</strong>
            <p>
              Si le vendeur refuse tout essai raisonnable, ne laisse pas consulter les documents ou presse la
              signature avec un autre acheteur supposé, je passe à l’annonce suivante. L’urgence du vendeur n’a
              pas à devenir le risque de l’acheteur.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'rappels',
      title: '11. Vérifier les rappels avec le VIN',
      content: (
        <>
          <p>
            RappelConso publie des campagnes concernant les voitures particulières. Je recherche le modèle et
            la période, puis je demande au réseau constructeur de vérifier avec le VIN si les opérations qui
            concernent l’exemplaire ont été réalisées. Une recherche par modèle seule peut produire trop ou
            pas assez de résultats : le numéro de série tranche.
          </p>
          <p>
            Un rappel n’est pas en soi un motif pour écarter la voiture. Une campagne non réalisée doit en
            revanche être comprise et planifiée avant l’achat, surtout lorsqu’elle concerne un organe de sécurité.
          </p>
        </>
      ),
    },
    {
      id: 'documents',
      title: '12. Sécuriser les documents et le paiement',
      content: (
        <>
          <p>
            Le jour de la vente, le vendeur remet notamment la carte grise barrée et signée, l’exemplaire du
            certificat de cession, un certificat de situation administrative de moins de quinze jours et le
            procès-verbal de contrôle technique lorsqu’il est requis. Les informations doivent correspondre à
            la voiture et au vendeur.
          </p>
          <p>
            Chez un professionnel, le bon de commande ou la facture doit détailler le véhicule, le kilométrage,
            le prix TTC et les prestations supplémentaires. La DGCCRF rappelle que ces prestations annexes ne
            peuvent pas être présentées comme obligatoires si elles ne le sont pas. Je refuse les lignes floues
            du type « pack livraison » sans contenu ni prix détaillé.
          </p>
          <p>
            Le mode de paiement est convenu à l’avance et vérifié auprès de la banque par un canal officiel.
            Je ne verse pas d’acompte significatif sur la seule base d’un message, d’une pièce d’identité envoyée
            en photo ou d’un lien de paiement reçu dans l’urgence.
          </p>
        </>
      ),
    },
    {
      id: 'garanties',
      title: 'Professionnel ou particulier : les garanties ne sont pas les mêmes',
      content: (
        <>
          <p>
            Lorsqu’un particulier achète à un vendeur professionnel, la garantie légale de conformité s’applique
            pendant deux ans à compter de la délivrance. Pour un bien d’occasion, la présomption d’antériorité du
            défaut dure un an : durant cette période, il appartient au professionnel de démontrer que le défaut
            n’existait pas à la livraison. Une garantie commerciale de trois, six ou douze mois peut s’ajouter ;
            elle ne remplace pas la garantie légale.
          </p>
          <p>
            Entre particuliers, la garantie légale de conformité ne s’applique pas. La garantie des vices cachés
            peut être invoquée si les conditions sont réunies, avec une charge de preuve plus lourde. Une baisse
            de prix ne compense donc pas automatiquement l’écart de protection : elle rémunère aussi un risque et
            davantage de vérifications.
          </p>
          <aside className="as-article-note as-note-fact">
            <strong>À retenir</strong>
            <p>
              Une mention « garantie 3 mois moteur-boîte-pont » décrit une garantie commerciale limitée. Elle
              ne supprime pas les garanties légales applicables lorsque le vendeur est un professionnel.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'checklist-finale',
      title: 'La check-list finale à imprimer',
      content: (
        <>
          <ul className="as-article-checklist as-print-checklist">
            <li>Identité du vendeur et VIN concordants</li>
            <li>Lien HistoVec consulté et kilométrages cohérents</li>
            <li>Factures et entretiens majeurs compris</li>
            <li>Contrôle technique complet lu, date valide</li>
            <li>Carrosserie, vitrages et dessous visibles examinés</li>
            <li>Quatre pneus relevés : dimensions, usure, date</li>
            <li>Deux clés et équipements testés</li>
            <li>Démarrage à froid observé</li>
            <li>Essai urbain, route irrégulière et vitesse stabilisée</li>
            <li>Usage réel testé : stationnement, sièges, bagages</li>
            <li>Rappels vérifiés avec le VIN</li>
            <li>Prix, frais, garanties, paiement et documents écrits</li>
          </ul>
          <p>
            Cette liste réduit les angles morts ; elle ne donne aucune certitude mécanique. Pour choisir la
            bonne référence avant d’examiner un exemplaire, revenez aux comparatifs <a href="/conseils/quelle-citadine-15000-euros/">
            citadine à 15 000 €</a> et <a href="/conseils/quel-suv-30000-euros/">SUV à 30 000 €</a>.
          </p>
        </>
      ),
    },
  ],
  sources: [
    {
      title: 'HistoVec — historique officiel du véhicule',
      publisher: 'Ministère de l’Intérieur',
      url: 'https://histovec.interieur.gouv.fr/histovec/accueil',
    },
    {
      title: 'Vendre ou donner son véhicule : documents et contrôle technique',
      publisher: 'Service-Public.fr',
      url: 'https://www.service-public.fr/particuliers/vosdroits/F1707',
    },
    {
      title: 'Achat d’un véhicule d’occasion : obligations du vendeur',
      publisher: 'DGCCRF — Ministère de l’Économie',
      url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/achat-dun-vehicule-doccasion-quelles-sont-les-obligations-du-vendeur',
    },
    {
      title: 'Véhicule automobile : contrôle technique',
      publisher: 'DGCCRF — Ministère de l’Économie',
      url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/vehicule-automobile-controle-technique',
    },
    {
      title: 'Garanties légales de conformité et contre les vices cachés',
      publisher: 'DGCCRF — Ministère de l’Économie',
      url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/les-garanties-legales-de-conformite-et-contre-les-vices-caches',
    },
    {
      title: 'Rappels de voitures particulières',
      publisher: 'RappelConso',
      url: 'https://rappel.conso.gouv.fr/categorie/0/9/voiture',
    },
  ],
  faqs: [
    {
      question: 'HistoVec suffit-il pour acheter une voiture d’occasion ?',
      answer:
        'Non. HistoVec fournit un historique administratif et des données issues notamment des contrôles techniques, mais pas le détail de toutes les réparations ni une inspection mécanique. Il doit être rapproché des factures, des documents, de l’essai et de l’état du véhicule.',
    },
    {
      question: 'Peut-on vendre une voiture sans contrôle technique ?',
      answer:
        'Une voiture de moins de quatre ans peut être vendue à un particulier sans contrôle technique. Au-delà, un procès-verbal de moins de six mois est généralement requis, avec un délai de contre-visite non dépassé. La vente à un professionnel suit un régime distinct.',
    },
    {
      question: 'La garantie de trois mois d’un garage remplace-t-elle la garantie légale ?',
      answer:
        'Non. Une garantie commerciale limitée s’ajoute aux garanties légales applicables ; elle ne les remplace pas. Pour un achat auprès d’un professionnel, la garantie légale de conformité peut être invoquée pendant deux ans selon ses conditions.',
    },
    {
      question: 'Faut-il faire inspecter une occasion par un professionnel ?',
      answer:
        'C’est prudent lorsque le prix, la complexité, l’historique incomplet ou un doute observé le justifient. Un contrôle indépendant peut réduire le risque, sans garantir qu’aucune panne future ne surviendra.',
    },
  ],
  relatedSlugs: [
    'quelle-citadine-15000-euros',
    'quel-suv-30000-euros',
    'essence-diesel-hybride-electrique',
  ],
};
